import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function EmployeeTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const [updating, setUpdating] = useState({});
  const [expanded, setExpanded] = useState(null);

  const loadTickets = (status) => {
    setLoading(true);
    api.get(`/admin/tickets?status=${status}`)
      .then(res => setTickets(res.data.tickets))
      .catch(() => toast.error('Could not load tickets'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTickets(filterStatus); }, [filterStatus]);

  const updateStatus = async (id, status) => {
    setUpdating(prev => ({ ...prev, [id]: true }));
    try {
      await api.patch(`/admin/tickets/${id}`, { status });
      toast.success('Ticket updated');
      loadTickets(filterStatus);
    } catch {
      toast.error('Could not update ticket');
    } finally {
      setUpdating(prev => ({ ...prev, [id]: false }));
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const STATUS_TABS = ['open', 'in_progress', 'resolved', 'all'];

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Support Tickets</div>
          <div className="page-sub">Manage tenant support requests across all properties.</div>

          {/* Status tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {STATUS_TABS.map(s => (
              <button key={s} className={`btn btn-sm ${filterStatus === s ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setFilterStatus(s)}>
                {s.replace('_', ' ')}
                {s === 'open' && tickets.length > 0 && filterStatus === 'open' && (
                  <span style={{ background: 'white', color: 'var(--navy)', borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '1px 6px', marginLeft: 6 }}>
                    {tickets.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Date</th><th>Tenant</th><th>Property / Unit</th><th>Subject</th><th>Category</th><th>Status</th><th>Update</th></tr>
                </thead>
                <tbody>
                  {tickets.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--green)' }}>
                      {filterStatus === 'open' ? '✅ No open tickets' : 'No tickets found'}
                    </td></tr>
                  )}
                  {tickets.map(t => (
                    <>
                      <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === t.id ? null : t.id)}>
                        <td style={{ fontSize: 12 }}>{fmtDate(t.created_at)}</td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{t.tenant_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{t.tenant_email}</div>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          <div>{t.property_name}</div>
                          <div style={{ color: 'var(--text-sub)' }}>{t.unit_number || '—'}</div>
                        </td>
                        <td style={{ fontSize: 13, fontWeight: 600 }}>
                          {t.subject}
                          <div style={{ fontSize: 11, color: 'var(--text-sub)', fontWeight: 400 }}>
                            {expanded === t.id ? '▲ Hide' : '▼ Show description'}
                          </div>
                        </td>
                        <td style={{ textTransform: 'capitalize', fontSize: 12 }}>{t.category}</td>
                        <td>
                          <span className={`badge badge-${t.status === 'resolved' ? 'green' : t.status === 'open' ? 'blue' : 'orange'}`}>
                            {t.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <select className="form-input" style={{ fontSize: 12, padding: '4px 8px', minWidth: 120 }}
                            value={t.status} onChange={e => updateStatus(t.id, e.target.value)}
                            disabled={updating[t.id]}>
                            <option value="open">Open</option>
                            <option value="in_progress">In progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                      </tr>
                      {expanded === t.id && (
                        <tr key={`${t.id}-desc`}>
                          <td colSpan={7} style={{ background: '#f7f9ff', padding: '12px 20px' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', marginBottom: 4 }}>Description:</div>
                            <div style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{t.description}</div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
